import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
    PageSection,
} from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import { useAuth } from "../../../contexts/AuthContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import { integrationsApi } from "../../../services/api";
import GlobalProviderModal from "./components/GlobalProviderModal";
import IntegrationConfigModal from "./components/IntegrationConfigModal";

const IntegrationsPage = () => {
    const { isGlobalAdmin, isTenantAdmin } = useAuth();
    const { selectedTenant, currentTenant, refreshTenant } = useTenantContext();

    const activeTenant = isGlobalAdmin ? selectedTenant : currentTenant;

    const [allProviders, setAllProviders] = useState([]);
    const [tenantSettings, setTenantSettings] = useState({ hrProviders: [], tillProviders: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);

    // Sync states
    const [syncing, setSyncing] = useState({
        locations: false,
        jobRoles: false,
        employees: false,
        clockRecords: false,
        troncRecords: false,
        fullHr: false,
    });
    const [syncResults, setSyncResults] = useState(null);

    // Modal states
    const [showProviderModal, setShowProviderModal] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [configuringProvider, setConfiguringProvider] = useState(null);

    const fetchAllProviders = async () => {
        try {
            const data = await integrationsApi.getProviders();
            if (data.success) {
                setAllProviders(data.data || []);
            }
        } catch (err) {
            console.error("Error fetching providers:", err);
        }
    };

    const fetchTenantSettings = useCallback(async () => {
        if (!activeTenant?.id) return;

        try {
            setLoading(true);
            const data = await integrationsApi.getSettingsOverview({ tenantId: activeTenant.id });
            if (data.success) {
                setTenantSettings({
                    hrProviders: data.data?.hrProviders || [],
                    tillProviders: data.data?.tillProviders || [],
                });
            }
        } catch (err) {
            setError(err.message || "Failed to fetch settings");
            console.error("Error fetching tenant settings:", err);
        } finally {
            setLoading(false);
        }
    }, [activeTenant?.id]);

    useEffect(() => {
        if (isGlobalAdmin) {
            fetchAllProviders();
        }
        fetchTenantSettings();
    }, [isGlobalAdmin, fetchTenantSettings]);

    const showNotification = (message, type = "success") => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // ========================================
    // SYNC HANDLERS
    // ========================================

    const handleSyncLocations = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, locations: true }));
        try {
            const response = await integrationsApi.syncLocations(activeTenant.id);
            if (response.success) {
                const d = response.data;
                const created = d.createdCount ?? d.recordsCreated ?? 0;
                const updated = d.updatedCount ?? d.recordsUpdated ?? 0;
                const skipped = d.skippedCount ?? d.recordsSkipped ?? 0;
                showNotification(
                    `Locations synced: ${created} created, ${updated} updated, ${skipped} skipped`
                );
                setSyncResults((prev) => ({ ...prev, locations: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, locations: false }));
            fetchTenantSettings();
        }
    };

    const handleSyncJobRoles = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, jobRoles: true }));
        try {
            const response = await integrationsApi.syncJobRoles(activeTenant.id);
            if (response.success) {
                const d = response.data;
                const created = d.createdCount ?? d.recordsCreated ?? 0;
                const updated = d.updatedCount ?? d.recordsUpdated ?? 0;
                showNotification(
                    `Job roles synced: ${created} created (TroncPoints=1), ${updated} updated`
                );
                setSyncResults((prev) => ({ ...prev, jobRoles: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, jobRoles: false }));
            fetchTenantSettings();
        }
    };

    const handleSyncEmployees = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, employees: true }));
        try {
            const response = await integrationsApi.syncEmployees(activeTenant.id);
            if (response.success) {
                const d = response.data;
                const created = d.createdCount ?? d.recordsCreated ?? 0;
                const updated = d.updatedCount ?? d.recordsUpdated ?? 0;
                showNotification(`Employees synced: ${created} created, ${updated} updated`);
                setSyncResults((prev) => ({ ...prev, employees: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, employees: false }));
            fetchTenantSettings();
        }
    };

    const handleSyncClockRecords = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, clockRecords: true }));
        try {
            const response = await integrationsApi.syncClockRecords({ tenantId: activeTenant.id });
            if (response.success) {
                const d = response.data;
                const created = d.createdCount ?? d.recordsCreated ?? 0;
                const updated = d.updatedCount ?? d.recordsUpdated ?? 0;
                const skipped = d.skippedCount ?? d.recordsSkipped ?? 0;
                showNotification(
                    `Clock records synced: ${created} created, ${updated} updated, ${skipped} skipped`
                );
                setSyncResults((prev) => ({ ...prev, clockRecords: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, clockRecords: false }));
            fetchTenantSettings();
        }
    };

    const handleSyncTroncRecords = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, troncRecords: true }));
        try {
            const response = await integrationsApi.syncTroncRecords({ tenantId: activeTenant.id });
            if (response.success) {
                const d = response.data;
                const created = d.createdCount ?? d.recordsCreated ?? 0;
                const updated = d.updatedCount ?? d.recordsUpdated ?? 0;
                const skipped = d.skippedCount ?? d.recordsSkipped ?? 0;
                showNotification(
                    `TRONC records synced: ${created} created, ${updated} updated, ${skipped} skipped`
                );
                setSyncResults((prev) => ({ ...prev, troncRecords: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, troncRecords: false }));
            fetchTenantSettings();
        }
    };

    const handleFullHrSync = async () => {
        if (!activeTenant?.id) return;
        setSyncing((prev) => ({ ...prev, fullHr: true }));
        try {
            const response = await integrationsApi.fullHrSync(activeTenant.id);
            if (response.success) {
                const d = response.data;
                const totalCreated = d.totalCreated ?? 0;
                const totalUpdated = d.totalUpdated ?? 0;
                showNotification(
                    `Full HR sync completed: ${totalCreated} created, ${totalUpdated} updated`
                );
                setSyncResults((prev) => ({ ...prev, fullHr: response.data }));
            } else {
                throw new Error(response.message || "Sync failed");
            }
        } catch (err) {
            showNotification(err.message, "error");
        } finally {
            setSyncing((prev) => ({ ...prev, fullHr: false }));
            fetchTenantSettings();
        }
    };

    // ========================================
    // PROVIDER MANAGEMENT (Global Admin)
    // ========================================

    const handleAddProvider = () => {
        setEditingProvider(null);
        setShowProviderModal(true);
    };

    const handleEditProvider = (provider) => {
        setEditingProvider(provider);
        setShowProviderModal(true);
    };

    const handleSaveProvider = async (providerData) => {
        try {
            const isEdit = !!editingProvider;
            let data;
            if (isEdit) {
                data = await integrationsApi.updateProvider(editingProvider.id, providerData);
            } else {
                data = await integrationsApi.createProvider(providerData);
            }
            if (!data.success) {
                throw new Error(data.message || `Failed to ${isEdit ? "update" : "create"} provider`);
            }
            showNotification(`Provider ${isEdit ? "updated" : "created"} successfully`);
            setShowProviderModal(false);
            fetchAllProviders();
        } catch (err) {
            showNotification(err.message, "error");
        }
    };

    const handleDeleteProvider = async (provider) => {
        if (!confirm(`Are you sure you want to delete the provider "${provider.name}"?`)) return;
        try {
            const data = await integrationsApi.deleteProvider(provider.id);
            if (!data.success) {
                throw new Error(data.message || "Failed to delete provider");
            }
            showNotification("Provider deleted successfully");
            fetchAllProviders();
        } catch (err) {
            showNotification(err.message, "error");
        }
    };

    // ========================================
    // TENANT SETTINGS
    // ========================================

    const handleConfigureProvider = (provider) => {
        setConfiguringProvider(provider);
        setShowConfigModal(true);
    };

    const handleSaveConfig = async (configData) => {
        try {
            const data = await integrationsApi.saveSettings({
                ...configData,
                tenantId: activeTenant?.id,
                integrationProviderId: configuringProvider.providerId,
            });
            if (!data.success) {
                throw new Error(data.message || "Failed to save configuration");
            }
            showNotification("Integration configured successfully");
            setShowConfigModal(false);
            fetchTenantSettings();

            // Refresh tenant data to update any integration-related status
            if (activeTenant?.id) {
                await refreshTenant(activeTenant.id);
            }
        } catch (err) {
            showNotification(err.message, "error");
        }
    };

    const breadcrumbs = [
        { label: "Dashboard", path: "/dashboard-overview" },
        { label: "System", path: "/system/integrations" },
        { label: "Integrations", path: "/system/integrations" },
    ];

    // Check if HR integration is enabled
    const hrEnabled = tenantSettings.hrProviders.some((p) => p.isEnabled);
    const tillEnabled = tenantSettings.tillProviders.some((p) => p.isEnabled);

    const formatLastSync = (dateString) => {
        if (!dateString) return "Never";
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    return (
        <>
            <Helmet>
                <title>Integrations - Rocket Tronc</title>
            </Helmet>

            <PageLayout
                title="Integrations"
                description="Configure HR and Till integrations for data synchronization."
                breadcrumbs={breadcrumbs}
            >
                {/* Notification */}
                {notification && (
                    <div className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}>
                        <Icon name={notification.type === "error" ? "AlertCircle" : "CheckCircle"} size={20} />
                        <span>{notification.message}</span>
                        <button onClick={() => setNotification(null)} className="btn btn-ghost btn-sm ml-auto">
                            <Icon name="X" size={16} />
                        </button>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="card p-12 text-center">
                        <div className="spinner mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading integrations...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="alert alert-error mb-6">
                        <Icon name="AlertCircle" size={20} />
                        <span>{error}</span>
                        <button onClick={fetchTenantSettings} className="btn btn-sm btn-outline ml-auto">
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <div className="space-y-8">
                        {/* SYNC SECTION */}
                        {(hrEnabled || tillEnabled) && (
                            <PageSection
                                title="Data Synchronization"
                                description="Sync data from your connected integrations. Locations are only created if they don't exist by name."
                            >
                                <div className="card">
                                    <div className="p-6 space-y-6">
                                        {/* HR Sync Options */}
                                        {hrEnabled && (
                                            <div>
                                                <h4 className="font-semibold mb-4 flex items-center gap-2">
                                                    <Icon name="Users" size={20} className="text-primary" />
                                                    HR Data Sync
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                    <button
                                                        onClick={handleSyncLocations}
                                                        disabled={syncing.locations}
                                                        className="btn btn-outline justify-start gap-2"
                                                    >
                                                        {syncing.locations ? (
                                                            <div className="spinner spinner-sm" />
                                                        ) : (
                                                            <Icon name="Building2" size={18} />
                                                        )}
                                                        Sync Locations
                                                    </button>
                                                    <button
                                                        onClick={handleSyncJobRoles}
                                                        disabled={syncing.jobRoles}
                                                        className="btn btn-outline justify-start gap-2"
                                                    >
                                                        {syncing.jobRoles ? (
                                                            <div className="spinner spinner-sm" />
                                                        ) : (
                                                            <Icon name="Briefcase" size={18} />
                                                        )}
                                                        Sync Job Roles
                                                    </button>
                                                    <button
                                                        onClick={handleSyncEmployees}
                                                        disabled={syncing.employees}
                                                        className="btn btn-outline justify-start gap-2"
                                                    >
                                                        {syncing.employees ? (
                                                            <div className="spinner spinner-sm" />
                                                        ) : (
                                                            <Icon name="UserPlus" size={18} />
                                                        )}
                                                        Sync Employees
                                                    </button>
                                                    <button
                                                        onClick={handleSyncClockRecords}
                                                        disabled={syncing.clockRecords}
                                                        className="btn btn-outline justify-start gap-2"
                                                    >
                                                        {syncing.clockRecords ? (
                                                            <div className="spinner spinner-sm" />
                                                        ) : (
                                                            <Icon name="Clock" size={18} />
                                                        )}
                                                        Sync Clock Records
                                                    </button>
                                                </div>
                                                <div className="mt-4">
                                                    <button
                                                        onClick={handleFullHrSync}
                                                        disabled={syncing.fullHr || Object.values(syncing).some(Boolean)}
                                                        className="btn btn-primary gap-2"
                                                    >
                                                        {syncing.fullHr ? (
                                                            <div className="spinner spinner-sm" />
                                                        ) : (
                                                            <Icon name="RefreshCw" size={18} />
                                                        )}
                                                        Full HR Sync (Locations → Job Roles → Employees)
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Till Sync Options */}
                                        {tillEnabled && (
                                            <div className="pt-4 border-t border-border">
                                                <h4 className="font-semibold mb-4 flex items-center gap-2">
                                                    <Icon name="CreditCard" size={20} className="text-primary" />
                                                    Till Data Sync
                                                </h4>
                                                <button
                                                    onClick={handleSyncTroncRecords}
                                                    disabled={syncing.troncRecords}
                                                    className="btn btn-outline gap-2"
                                                >
                                                    {syncing.troncRecords ? (
                                                        <div className="spinner spinner-sm" />
                                                    ) : (
                                                        <Icon name="Banknote" size={18} />
                                                    )}
                                                    Sync TRONC Records
                                                </button>
                                            </div>
                                        )}

                                        {/* Sync Status Info */}
                                        <div className="pt-4 border-t border-border">
                                            <p className="text-sm text-muted-foreground">
                                                <Icon name="Info" size={14} className="inline mr-1" />
                                                <strong>Note:</strong> New locations are only created if no existing location has the same name.
                                                New job roles are imported with a default Tronc Score of 1.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </PageSection>
                        )}

                        {/* HR PROVIDERS SECTION */}
                        <PageSection
                            title="HR Integrations"
                            description="Connect your HR system to sync employees, job roles, locations, and clock records."
                        >
                            <div className="card">
                                {tenantSettings.hrProviders.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Icon name="Users" size={32} className="mx-auto mb-3 text-muted-foreground" />
                                        <p className="text-muted-foreground">No HR providers available</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {tenantSettings.hrProviders.map((provider) => (
                                            <div key={provider.providerId} className="p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                                                        {provider.logoUrl ? (
                                                            <img src={provider.logoUrl} alt={provider.providerName} className="w-8 h-8 object-contain" />
                                                        ) : (
                                                            <Icon name="Users" size={24} className="text-muted-foreground" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold">{provider.providerName}</h4>
                                                        <p className="text-sm text-muted-foreground">
                                                            {provider.isEnabled ? (
                                                                <>
                                                                    <span className="text-success">Connected</span>
                                                                    {provider.lastSyncedOn && (
                                                                        <span className="ml-2">• Last sync: {formatLastSync(provider.lastSyncedOn)}</span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "Not configured"
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`badge ${provider.isEnabled ? "badge-success" : "badge-neutral"}`}>
                                                        {provider.isEnabled ? "Enabled" : "Disabled"}
                                                    </span>
                                                    <button
                                                        onClick={() => handleConfigureProvider(provider)}
                                                        className="btn btn-ghost btn-sm"
                                                    >
                                                        <Icon name="Settings" size={16} />
                                                        Configure
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </PageSection>

                        {/* TILL PROVIDERS SECTION */}
                        <PageSection
                            title="Till / POS Integrations"
                            description="Connect your Till/POS system to sync TRONC and service charge data."
                        >
                            <div className="card">
                                {tenantSettings.tillProviders.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Icon name="CreditCard" size={32} className="mx-auto mb-3 text-muted-foreground" />
                                        <p className="text-muted-foreground">No Till providers available</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-border">
                                        {tenantSettings.tillProviders.map((provider) => (
                                            <div key={provider.providerId} className="p-4 flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                                                        {provider.logoUrl ? (
                                                            <img src={provider.logoUrl} alt={provider.providerName} className="w-8 h-8 object-contain" />
                                                        ) : (
                                                            <Icon name="CreditCard" size={24} className="text-muted-foreground" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold">{provider.providerName}</h4>
                                                        <p className="text-sm text-muted-foreground">
                                                            {provider.isEnabled ? (
                                                                <>
                                                                    <span className="text-success">Connected</span>
                                                                    {provider.lastSyncedOn && (
                                                                        <span className="ml-2">• Last sync: {formatLastSync(provider.lastSyncedOn)}</span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "Not configured"
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`badge ${provider.isEnabled ? "badge-success" : "badge-neutral"}`}>
                                                        {provider.isEnabled ? "Enabled" : "Disabled"}
                                                    </span>
                                                    <button
                                                        onClick={() => handleConfigureProvider(provider)}
                                                        className="btn btn-ghost btn-sm"
                                                    >
                                                        <Icon name="Settings" size={16} />
                                                        Configure
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </PageSection>

                        {/* GLOBAL ADMIN SECTION */}
                        {isGlobalAdmin && (
                            <PageSection
                                title="Provider Management (Global Admin)"
                                description="Manage available integration providers across all tenants."
                            >
                                <div className="flex justify-end mb-4">
                                    <button onClick={handleAddProvider} className="btn btn-primary btn-sm">
                                        <Icon name="Plus" size={16} />
                                        Add Provider
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* HR Providers */}
                                    <div className="card">
                                        <div className="p-4 border-b border-border">
                                            <h4 className="font-semibold">HR Providers</h4>
                                        </div>
                                        {allProviders.filter((p) => p.type === 0).length === 0 ? (
                                            <div className="p-8 text-center">
                                                <p className="text-muted-foreground">No HR providers</p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {allProviders
                                                    .filter((p) => p.type === 0)
                                                    .map((provider) => (
                                                        <div key={provider.id} className="p-4 flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium">{provider.name}</p>
                                                                <p className="text-sm text-muted-foreground">Code: {provider.code}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`badge ${provider.isActive ? "badge-success" : "badge-neutral"}`}>
                                                                    {provider.isActive ? "Active" : "Inactive"}
                                                                </span>
                                                                <button onClick={() => handleEditProvider(provider)} className="btn btn-ghost btn-sm">
                                                                    <Icon name="Pencil" size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteProvider(provider)}
                                                                    className="btn btn-ghost btn-sm text-error"
                                                                >
                                                                    <Icon name="Trash2" size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Till Providers */}
                                    <div className="card">
                                        <div className="p-4 border-b border-border">
                                            <h4 className="font-semibold">Till / POS Providers</h4>
                                        </div>
                                        {allProviders.filter((p) => p.type === 1).length === 0 ? (
                                            <div className="p-8 text-center">
                                                <p className="text-muted-foreground">No Till providers</p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {allProviders
                                                    .filter((p) => p.type === 1)
                                                    .map((provider) => (
                                                        <div key={provider.id} className="p-4 flex items-center justify-between">
                                                            <div>
                                                                <p className="font-medium">{provider.name}</p>
                                                                <p className="text-sm text-muted-foreground">Code: {provider.code}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={`badge ${provider.isActive ? "badge-success" : "badge-neutral"}`}>
                                                                    {provider.isActive ? "Active" : "Inactive"}
                                                                </span>
                                                                <button onClick={() => handleEditProvider(provider)} className="btn btn-ghost btn-sm">
                                                                    <Icon name="Pencil" size={16} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteProvider(provider)}
                                                                    className="btn btn-ghost btn-sm text-error"
                                                                >
                                                                    <Icon name="Trash2" size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </PageSection>
                        )}
                    </div>
                )}

                {/* Modals */}
                {showProviderModal && (
                    <GlobalProviderModal
                        provider={editingProvider}
                        onClose={() => setShowProviderModal(false)}
                        onSave={handleSaveProvider}
                    />
                )}

                {showConfigModal && configuringProvider && (
                    <IntegrationConfigModal
                        provider={configuringProvider}
                        onClose={() => setShowConfigModal(false)}
                        onSave={handleSaveConfig}
                    />
                )}
            </PageLayout>
        </>
    );
};

export default IntegrationsPage;
